import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { catchError, tap, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface Category {
  id: string;
  name: string;
}

export interface PlatformConfig {
  config: {
    categories: Category[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class PlatformConfigService {
  private readonly configUrl = `${environment.apiUrl}/platform/config`;
  private configSubject = new BehaviorSubject<PlatformConfig | null>(null);
  private configCache$: Observable<PlatformConfig> | null = null;

  public config$ = this.configSubject.asObservable();

  constructor(private http: HttpClient) {
    console.log('🔧 PlatformConfigService initialized');
  }

  /**
   * Load platform configuration from API
   * This method caches the result and returns the same observable for multiple calls
   */
  loadConfig(): Observable<PlatformConfig> {
    console.log('🚀 [PlatformConfigService] Loading platform configuration...');
    
    if (this.configCache$) {
      console.log('📦 [PlatformConfigService] Returning cached config observable');
      return this.configCache$;
    }

    this.configCache$ = this.http.get<PlatformConfig>(this.configUrl).pipe(
      tap(config => {
        console.log('✅ [PlatformConfigService] Platform config loaded:', config);
        this.configSubject.next(config);
      }),
      catchError(error => {
        console.error('❌ [PlatformConfigService] Failed to load platform config:', error);
        // Return empty config on error to prevent app from breaking
        const emptyConfig: PlatformConfig = {
          config: {
            categories: []
          }
        };
        this.configSubject.next(emptyConfig);
        return of(emptyConfig);
      }),
      shareReplay(1) // Cache the result
    );

    return this.configCache$;
  }

  /**
   * Get current configuration synchronously
   */
  getConfig(): PlatformConfig | null {
    return this.configSubject.value;
  }

  /**
   * Get a specific configuration value
   */
  getConfigValue<T>(key: string, defaultValue: T): T {
    const config = this.getConfig();
    if (!config) {
      return defaultValue;
    }
    
    // Handle nested keys like 'features.darkMode'
    const keys = key.split('.');
    let value: any = config;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return defaultValue;
      }
    }
    
    return value !== undefined && value !== null ? (value as T) : defaultValue;
  }

  /**
   * Check if configuration is loaded
   */
  isConfigLoaded(): boolean {
    return this.configSubject.value !== null;
  }

  /**
   * Get all categories from platform configuration
   */
  getCategories(): Category[] {
    const config = this.getConfig();
    return config?.config?.categories || [];
  }

  /**
   * Get category by ID
   */
  getCategoryById(id: string): Category | undefined {
    const categories = this.getCategories();
    return categories.find(category => category.id === id);
  }

  /**
   * Get category by name
   */
  getCategoryByName(name: string): Category | undefined {
    const categories = this.getCategories();
    return categories.find(category => category.name.toLowerCase() === name.toLowerCase());
  }

  /**
   * Check if a category exists by ID
   */
  categoryExists(id: string): boolean {
    return this.getCategoryById(id) !== undefined;
  }

  /**
   * Get category names as a simple array
   */
  getCategoryNames(): string[] {
    return this.getCategories().map(category => category.name);
  }
}